import '@/assets/styles/components/account-information-modal.styles.scss';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInput, Button, Modal } from '@/components';
import { schema } from './account-information-modal.schema';
import { AccountFormData, AccountInfoFormProps } from './account-information-modal.types';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider } from '@/hooks';
import { ExpertPaymentInfoCreateRequest } from '@dopingtech/live_expert_bff_api_ts';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useQueryClient } from 'react-query';

const AccountInformationModal: React.FC<AccountInfoFormProps> = ({ isOpen, onClose, accountInformationData }) => {
	const queryClient = useQueryClient();
	const { PaymentInfoControllerApi } = useApi();

	const createPaymentInfo = useMutationProvider((body: ExpertPaymentInfoCreateRequest) => PaymentInfoControllerApi.createPaymentInfo(body), {
		onSuccess: () => {
			onClose();
			queryClient.refetchQueries(QUERY_KEYS.getLoggedInExpertDetailInfo);
		}
	});

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting, isValid },
		setValue,
		watch
	} = useForm<AccountFormData>({
		resolver: yupResolver(schema),
		mode: 'onChange',
		values: {
			bank: accountInformationData?.bankName || '',
			firstName: accountInformationData?.accountHolderName || '',
			lastName: accountInformationData?.accountHolderSurname || '',
			identityNumber: accountInformationData?.accountHolderIdentityNumber || '',
			iban: accountInformationData?.iban || ''
		}
	});

	const watchedValues = watch();

	const isEditMode = useMemo(() => !!accountInformationData?.iban, [accountInformationData]);

	const hasChanges = useMemo(() => {
		if (!accountInformationData?.iban) {
			return Object.values(watchedValues).some(value => value !== '');
		}

		return (
			watchedValues.bank !== accountInformationData.bankName ||
			watchedValues.firstName !== accountInformationData.accountHolderName ||
			watchedValues.lastName !== accountInformationData.accountHolderSurname ||
			watchedValues.identityNumber !== accountInformationData.accountHolderIdentityNumber ||
			watchedValues.iban !== accountInformationData.iban
		);
	}, [watchedValues, accountInformationData]);

	const onSubmit = async (data: AccountFormData) => {
		createPaymentInfo.mutateAsync({
			bankName: data.bank,
			accountHolderName: data.firstName,
			accountHolderSurname: data.lastName,
			accountHolderIdentityNumber: data.identityNumber,
			iban: data.iban
		});
	};

	const handleInputChange = (field: keyof AccountFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
		let value = e.target.value;

		if (field === 'firstName' || field === 'lastName') {
			value = value.replace(/[0-9]/g, '');
		}

		if (field === 'iban') {
			value = value.replace(/[^0-9]/g, '');
		}

		setValue(field, value, { shouldValidate: true });
	};

	return (
		<Modal
			isOpen={isOpen}
			closeOnEsc={false}
			closeOnClickOutside={false}
			onClose={onClose}
			size="md"
			showCloseButton={isEditMode}
			className="account-info-modal">
			<div className="modal-header">
				<h2>{isEditMode ? 'Kart Bilgilerini Düzenle' : 'Hesap Bilgileri'}</h2>
				<p>Hesabının aktif olabilmesi ve ödeme alabilmeniz için banka bilgilerinizi eksiksiz ve doğru bir şekilde girin.</p>
			</div>

			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="form-group">
					<h3>Banka</h3>
					<FormInput
						{...register('bank')}
						value={watchedValues.bank || ''}
						placeholder="Kullandığınız banka adını yazın"
						error={Boolean(errors.bank)}
						onChange={handleInputChange('bank')}
					/>
				</div>

				<div className="name-inputs">
					<div className="form-group">
						<h3>Hesap Sahibi</h3>
						<FormInput
							{...register('firstName')}
							value={watchedValues.firstName || ''}
							placeholder="Ad"
							error={Boolean(errors.firstName)}
							onChange={handleInputChange('firstName')}
						/>
					</div>

					<div className="form-group">
						<h3>&nbsp;</h3>
						<FormInput
							{...register('lastName')}
							value={watchedValues.lastName || ''}
							placeholder="Soyad"
							error={Boolean(errors.lastName)}
							onChange={handleInputChange('lastName')}
						/>
					</div>
				</div>

				<div className="form-group">
					<h3>Hesap Sahibi TC</h3>
					<FormInput
						{...register('identityNumber')}
						value={watchedValues.identityNumber || ''}
						placeholder="TC kimlik numaranızı yazın"
						variant="number"
						maxLength={11}
						error={Boolean(errors.identityNumber)}
						onChange={handleInputChange('identityNumber')}
					/>
				</div>

				<div className="form-group">
					<h3>IBAN</h3>
					<FormInput
						{...register('iban')}
						value={watchedValues.iban || ''}
						variant="iban"
						placeholder="IBAN bilginizi yazın"
						error={Boolean(errors.iban)}
						onChange={handleInputChange('iban')}
					/>
				</div>

				<Button
					type="submit"
					variant="blue"
					fullWidth
					disabled={!isValid || isSubmitting || !hasChanges}>
					{isEditMode ? 'Değişiklikleri Kaydet' : 'Hesap Bilgilerimi Onayla'}
				</Button>
			</form>
		</Modal>
	);
};

export default AccountInformationModal;
